import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import * as api from "./mediaApi";

export const fetchMedia = createAsyncThunk("media/fetchMedia", async (params, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.getMedia(params, token);
    return res.data?.data || res.data;
  } catch (err) {
    return rejectWithValue(err?.response?.data?.message || err?.message || "Failed to fetch media");
  }
});

export const uploadMedia = createAsyncThunk("media/uploadMedia", async (formData, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.uploadMedia(formData, token);
    toast.success("File uploaded successfully");
    return res.data?.data || res.data;
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to upload file";
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

export const updateMedia = createAsyncThunk("media/updateMedia", async ({ id, data }, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    const res = await api.updateMedia(id, data, token);
    toast.success("Media updated successfully");
    return res.data;
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to update media";
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

export const deleteMedia = createAsyncThunk("media/deleteMedia", async (id, { getState, rejectWithValue }) => {
  try {
    const token = getState().user.user.token;
    await api.deleteMedia(id, token);
    toast.success("Media deleted successfully");
    return id;
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || "Failed to delete media";
    toast.error(msg);
    return rejectWithValue(msg);
  }
});

const mediaSlice = createSlice({
  name: "media",
  initialState: {
    media: [],
    total: 0,
    page: 1,
    pages: 0,
    loading: false,
    error: null,
    uploading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMedia.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchMedia.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.media = payload.media || payload || [];
        state.total = payload.total || 0;
        state.page = payload.page || 1;
        state.pages = payload.pages || 0;
      })
      .addCase(fetchMedia.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(uploadMedia.pending, (state) => { state.uploading = true; })
      .addCase(uploadMedia.fulfilled, (state, action) => {
        state.uploading = false;
        const item = action.payload?.media || action.payload;
        if (item?._id) state.media.unshift(item);
      })
      .addCase(uploadMedia.rejected, (state, action) => { state.uploading = false; state.error = action.payload; })

      .addCase(updateMedia.fulfilled, (state, action) => {
        const item = action.payload?.media || action.payload;
        if (item?._id) {
          const idx = state.media.findIndex((m) => m._id === item._id);
          if (idx !== -1) state.media[idx] = item;
        }
      })
      .addCase(updateMedia.rejected, (state, action) => { state.error = action.payload; })

      .addCase(deleteMedia.fulfilled, (state, action) => {
        state.media = state.media.filter((m) => m._id !== action.payload);
      })
      .addCase(deleteMedia.rejected, (state, action) => { state.error = action.payload; });
  },
});

export default mediaSlice.reducer;
